import { type FC, useRef, useState, useEffect } from "react";
import { useLocale, useScrollBlock } from "hooks";
import clsx from "clsx";
import translation from "./translation";
import {
  TelegramShareButton,
  LinkedinShareButton,
  // WhatsappShareButton,
} from "react-share";
import { Loading } from "components";
import { toast } from "react-toastify";
import Toast from "components/Toast";
import style from "./SharingModal.module.scss";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  item: any;
  getLinkAndUpdateShareLinkList: (
    item: any
  ) => Promise<{ raw: string; hashed: string }>;
}

const SharingModal: FC<IProps> = ({
  isOpen,
  onClose,
  item,
  getLinkAndUpdateShareLinkList,
}) => {
  const modalRef = useRef(null);
  const [blockScroll, allowScroll] = useScrollBlock();
  const { locale, isRTL } = useLocale();
  const [invisible, setInvisible] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shareLink, setShareLink] = useState<{ raw: string; hashed: string }>();

  useEffect(() => {
    if (isOpen) {
      setInvisible(false);
      blockScroll();
    } else {
      allowScroll();
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setInvisible(true);
    }, 300);
  };

  useEffect(() => {
    const cb = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", cb);
    return () => document.removeEventListener("keydown", cb);
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const link = await getLinkAndUpdateShareLinkList(item);
      setShareLink(link);
      setIsLoading(false);
    })();
  }, [item]);

  function unsecuredCopyToClipboard(text: string) {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.prepend(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
      toast.success(translation[locale]?.COPIED);
    } catch (err) {
      console.error("Unable to copy to clipboard", err);
    }
    document.body.removeChild(textArea);
  }

  const copyToClipboard = async () => {
    const target = shareLink.hashed || shareLink.raw;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(target);
      } else {
        unsecuredCopyToClipboard(target);
      }
    } finally {
      toast.success(translation[locale]?.COPIED);
    }
  };

  return (
    <div
      className={clsx(
        `print:tw-hidden tw-fixed tw-bottom-0 tw-left-0 tw-w-full tw-h-screen md:tw-h-[calc(100vh-108px)] xxxl:tw-h-[calc(100vh-144px)] tw-bg-[#000000]/90 md:tw-bg-[rgba(0,0,0,.85)] tw-flex tw-items-center tw-justify-center tw-z-[100000] tw-transition-opacity tw-duration-300`,
        {
          "tw-invisible": invisible,
          "tw-opacity-0": !isOpen,
          "tw-opacity-100": isOpen,
        },
        style.page
      )}
      ref={modalRef}
      //   onClick={handleCloseModal}
    >
      <div className="tw-max-w-[90%] md:tw-max-w-[340px] tw-w-full tw-bg-white tw-rounded-2xl tw-relative">
        <div className="tw-flex tw-justify-between tw-p-5 tw-border-b tw-border-x-0 tw-border-t-0 tw-border-[#E6E6EF] tw-border-solid">
          <h6 className="tw-text-base tw-font-medium">
            {translation[locale]?.SHARE}
          </h6>
          <button
            onClick={handleClose}
            className={clsx(
              "tw-bg-zero tw-border-0 tw-cursor-pointer tw-absolute",
              {
                "tw-left-5": isRTL,
                "tw-right-5": !isRTL,
              }
            )}
            name="close-share-modal"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Menu-Close_MD">
                <path
                  id="Vector"
                  d="M18 18L12 12M12 12L6 6M12 12L18 6M12 12L6 18"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </button>
        </div>
        <div className="tw-flex tw-justify-between tw-flex-wrap md:tw-flex-nowrap tw-items-center tw-py-4">
          <div className="tw-flex tw-justify-between tw-w-full tw-mt-4 md:tw-mt-0">
            <button
              onClick={copyToClipboard}
              className={clsx(
                "tw-w-[70px] tw-h-[70px] tw-flex tw-justify-center tw-items-center tw-border-0 tw-bg-zero tw-cursor-pointer",
                "social-share-link"
              )}
            >
              <svg
                width="23"
                height="24"
                viewBox="0 0 23 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.27345 20.0255C2.57908 18.4999 2.44191 15.8825 3.96753 14.1881L7.53622 10.2247L5.65359 8.52953L2.08489 12.493C0.901797 13.8069 0.289127 15.5371 0.381663 17.3027C0.474199 19.0684 1.26436 20.725 2.57832 21.9081C3.89228 23.0912 5.62241 23.7039 7.3881 23.6114C9.15379 23.5188 10.8104 22.7287 11.9935 21.4147L15.5622 17.4513L13.6796 15.7561L10.1109 19.7196C8.58524 21.4139 5.96782 21.5511 4.27345 20.0255ZM8.92223 16.8552L16.0596 8.92827L14.0779 7.14392L6.94051 15.0708L8.92223 16.8552ZM11.0066 2.58437L7.43794 6.54781L9.32057 8.24294L12.8893 4.2795C14.4149 2.58513 17.0323 2.44796 18.7267 3.97357C20.421 5.49919 20.5582 8.11661 19.0326 9.81098L15.4639 13.7744L17.3465 15.4695L20.9152 11.5061C22.0983 10.1921 22.711 8.46202 22.6185 6.69633C22.5259 4.93065 21.7358 3.27404 20.4218 2.09094C19.1078 0.907845 17.3777 0.295173 15.612 0.387709C13.8463 0.480246 12.1897 1.27041 11.0066 2.58437Z"
                  fill="#113E66"
                />
              </svg>
            </button>
            <TelegramShareButton
              url={shareLink?.hashed || shareLink?.raw || ""}
            >
              <a
                target="_blank"
                className={clsx(
                  "tw-w-[70px] tw-h-[70px] tw-flex tw-justify-center tw-items-center",
                  "social-share-link"
                )}
              >
                <svg
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_52_8923)">
                    <path
                      d="M12.2144 24.7144C18.8418 24.7144 24.2144 19.3418 24.2144 12.7144C24.2144 6.08694 18.8418 0.714355 12.2144 0.714355C5.58694 0.714355 0.214355 6.08694 0.214355 12.7144C0.214355 19.3418 5.58694 24.7144 12.2144 24.7144Z"
                      fill="url(#paint0_linear_52_8923)"
                    />
                    <path
                      d="M8.33716 13.5917L9.76086 17.5323C9.76086 17.5323 9.93886 17.901 10.1295 17.901C10.3201 17.901 13.155 14.9518 13.155 14.9518L16.3075 8.86279L8.38796 12.5745L8.33716 13.5917Z"
                      fill="#C8DAEA"
                    />
                    <path
                      d="M10.2249 14.6021L9.95156 17.5066C9.95156 17.5066 9.83716 18.3967 10.727 17.5066C11.6168 16.6167 12.4685 15.9303 12.4685 15.9303"
                      fill="#A9C6D8"
                    />
                    <path
                      d="M8.36306 13.7321L5.43446 12.7779C5.43446 12.7779 5.08446 12.6359 5.19716 12.3139C5.22036 12.2475 5.26716 12.191 5.40716 12.0939C6.05606 11.6416 17.4178 7.55795 17.4178 7.55795C17.4178 7.55795 17.7386 7.44985 17.9278 7.52175C17.9746 7.53624 18.0167 7.5629 18.0498 7.599C18.0829 7.63511 18.1059 7.67937 18.1163 7.72725C18.1367 7.81181 18.1453 7.89882 18.1417 7.98575C18.1408 8.06095 18.1317 8.13065 18.1248 8.23995C18.0556 9.35645 15.9848 17.6892 15.9848 17.6892C15.9848 17.6892 15.8609 18.1768 15.417 18.1935C15.3079 18.1971 15.1992 18.1786 15.0974 18.1392C14.9956 18.0998 14.9028 18.0404 14.8245 17.9643C13.9534 17.215 10.9426 15.1916 10.2773 14.7466C10.2623 14.7364 10.2496 14.7231 10.2402 14.7076C10.2308 14.692 10.2248 14.6747 10.2227 14.6566C10.2134 14.6097 10.2644 14.5516 10.2644 14.5516C10.2644 14.5516 15.507 9.89165 15.6465 9.40245C15.6573 9.36455 15.6165 9.34585 15.5617 9.36245C15.2135 9.49055 9.17726 13.3024 8.51106 13.7231C8.46311 13.7377 8.41242 13.7407 8.36306 13.7321Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <linearGradient
                      id="paint0_linear_52_8923"
                      x1="12.2144"
                      y1="24.7144"
                      x2="12.2144"
                      y2="0.714355"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#1D93D2" />
                      <stop offset="1" stopColor="#38B0E3" />
                    </linearGradient>
                    <clipPath id="clip0_52_8923">
                      <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0.214355 0.714355)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </a>
            </TelegramShareButton>

            <LinkedinShareButton
              url={shareLink?.hashed || shareLink?.raw || ""}
            >
              <a
                target="_blank"
                className={clsx(
                  "tw-w-[70px] tw-h-[70px] tw-flex tw-justify-center tw-items-center",
                  "social-share-link"
                )}
              >
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="tw-fill-[#0A66C2]"
                >
                  <g clipPath="url(#clip1_790_3942)">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M4.53765 0.79248H15.5556C16.758 0.795932 17.9101 1.27511 18.7603 2.12534C19.6106 2.97557 20.0898 4.12773 20.0932 5.33013V16.2565C20.0893 17.4586 19.61 18.6103 18.7598 19.4602C17.9096 20.3101 16.7577 20.789 15.5556 20.7925H4.53765C3.33553 20.789 2.18362 20.3101 1.33343 19.4602C0.483254 18.6103 0.00388261 17.4586 0 16.2565V5.33013C0.00345147 4.12773 0.482632 2.97557 1.33286 2.12534C2.18309 1.27511 3.33525 0.795932 4.53765 0.79248V0.79248Z"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8.04351 8.54489H10.583V9.85304H10.6189C10.9738 9.21858 11.8371 8.54489 13.1273 8.54489C15.8074 8.54489 16.3028 10.2128 16.3028 12.3827V16.971H13.6555V12.8929C13.6555 11.9592 13.6359 10.7573 12.2786 10.7573C10.9214 10.7573 10.6892 11.7727 10.6892 12.8258V16.9792H8.04351V8.54489ZM6.54077 5.99072C6.54077 6.26303 6.46002 6.52923 6.30873 6.75565C6.15744 6.98206 5.94241 7.15854 5.69083 7.26274C5.43925 7.36695 5.16241 7.39422 4.89533 7.34109C4.62825 7.28797 4.38293 7.15684 4.19037 6.96429C3.99782 6.77173 3.86669 6.52641 3.81357 6.25933C3.76044 5.99225 3.78771 5.71541 3.89191 5.46383C3.99612 5.21225 4.1726 4.99722 4.39901 4.84593C4.62543 4.69464 4.89163 4.61389 5.16394 4.61389C5.52896 4.61432 5.87891 4.75952 6.13703 5.01763C6.39514 5.27575 6.54034 5.6257 6.54077 5.99072V5.99072ZM3.79038 8.54489H6.54077V16.971H3.79038V8.54489Z"
                      className="tw-fill-white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip1_790_3942">
                      <rect
                        width="20.0932"
                        height="20"
                        fill="white"
                        transform="translate(0 0.79248)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </a>
            </LinkedinShareButton>
            {/* <WhatsappShareButton
              url={decodeURI(shareLink?.raw) || shareLink?.hashed || ""}
            >
              <a
                target="_blank"
                className={clsx(
                  "tw-w-[70px] tw-h-[70px] tw-flex tw-justify-center tw-items-center",
                  "social-share-link"
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="tw-fill-[#344456]/50"
                >
                  <rect
                    width="24"
                    height="24"
                    rx="5"
                    fill="url(#paint1_linear_1499_7853)"
                  />
                  <path
                    xmlns="http://www.w3.org/2000/svg"
                    d="M0 5C0 2.23858 2.23858 0 5 0H19C21.7614 0 24 2.23858 24 5V19C24 21.7614 21.7614 24 19 24H5C2.23858 24 0 21.7614 0 19V5Z"
                    className="tw-fill-zero"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.0472 5.91684C16.4426 4.31035 14.3087 3.42525 12.0352 3.42427C7.35064 3.42427 3.53795 7.23671 3.53607 11.9227C3.53549 13.4206 3.92677 14.8827 4.67057 16.1717L3.46484 20.5757L7.9703 19.3939C9.21168 20.0709 10.6093 20.4279 12.0317 20.4284H12.0353C16.7193 20.4284 20.5323 16.6154 20.5343 11.9293C20.5351 9.65855 19.6518 7.52324 18.0472 5.91684ZM12.0353 18.993H12.0324C10.7649 18.9925 9.52159 18.652 8.43706 18.0083L8.17907 17.8553L5.50546 18.5566L6.21908 15.9499L6.05112 15.6825C5.34404 14.5579 4.97051 13.258 4.97108 11.9231C4.97263 8.02835 8.14153 4.85969 12.0381 4.85969C13.9248 4.86035 15.6985 5.59605 17.0322 6.93138C18.3659 8.26663 19.0999 10.0414 19.0992 11.9289C19.0976 15.8241 15.9287 18.993 12.0353 18.993ZM15.91 13.7023C15.6976 13.5961 14.6537 13.0823 14.459 13.0114C14.2644 12.9407 14.1227 12.9052 13.9812 13.1178C13.8396 13.3304 13.4326 13.8087 13.3087 13.9505C13.1848 14.0921 13.0609 14.11 12.8486 14.0036C12.6362 13.8973 11.952 13.673 11.1408 12.9495C10.5096 12.3865 10.0834 11.6911 9.95948 11.4784C9.8356 11.2658 9.94623 11.1509 10.0526 11.045C10.1481 10.9498 10.265 10.7969 10.3712 10.6729C10.4774 10.549 10.5127 10.4603 10.5836 10.3186C10.6544 10.1769 10.619 10.0528 10.5658 9.94663C10.5127 9.84024 10.088 8.79495 9.91107 8.36974C9.7387 7.95573 9.56362 8.01175 9.43328 8.0052C9.30956 7.99907 9.16785 7.99768 9.02622 7.99768C8.88467 7.99768 8.65465 8.05083 8.45995 8.26344C8.26533 8.47605 7.71673 8.98982 7.71673 10.0351C7.71673 11.0804 8.47761 12.0901 8.58384 12.2319C8.68997 12.3736 10.0813 14.5186 12.2116 15.4385C12.7181 15.6573 13.1137 15.7879 13.4222 15.8859C13.931 16.0475 14.3939 16.0246 14.7597 15.97C15.1678 15.909 16.0161 15.4562 16.1931 14.9602C16.3701 14.4641 16.3701 14.0389 16.317 13.9503C16.2639 13.8619 16.1224 13.8087 15.91 13.7023Z"
                    className="tw-fill-white"
                  />
                  <defs>
                    <linearGradient
                      id="paint1_linear_1499_7853"
                      x1="18.2749"
                      y1="-4.38058e-07"
                      x2="6.82169"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#61FD7D" />
                      <stop offset="1" stopColor="#27D045" />
                    </linearGradient>
                  </defs>
                </svg>
              </a>
            </WhatsappShareButton> */}
            <a
              target="_blank"
              href={`https://wa.me/?text=${decodeURI(shareLink?.raw)}`}
              className={clsx(
                "tw-w-[70px] tw-h-[70px] tw-flex tw-justify-center tw-items-center",
                "social-share-link"
              )}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="tw-fill-[#344456]/50"
              >
                <rect
                  width="24"
                  height="24"
                  rx="5"
                  fill="url(#paint1_linear_1499_7853)"
                />
                <path
                  xmlns="http://www.w3.org/2000/svg"
                  d="M0 5C0 2.23858 2.23858 0 5 0H19C21.7614 0 24 2.23858 24 5V19C24 21.7614 21.7614 24 19 24H5C2.23858 24 0 21.7614 0 19V5Z"
                  className="tw-fill-zero"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.0472 5.91684C16.4426 4.31035 14.3087 3.42525 12.0352 3.42427C7.35064 3.42427 3.53795 7.23671 3.53607 11.9227C3.53549 13.4206 3.92677 14.8827 4.67057 16.1717L3.46484 20.5757L7.9703 19.3939C9.21168 20.0709 10.6093 20.4279 12.0317 20.4284H12.0353C16.7193 20.4284 20.5323 16.6154 20.5343 11.9293C20.5351 9.65855 19.6518 7.52324 18.0472 5.91684ZM12.0353 18.993H12.0324C10.7649 18.9925 9.52159 18.652 8.43706 18.0083L8.17907 17.8553L5.50546 18.5566L6.21908 15.9499L6.05112 15.6825C5.34404 14.5579 4.97051 13.258 4.97108 11.9231C4.97263 8.02835 8.14153 4.85969 12.0381 4.85969C13.9248 4.86035 15.6985 5.59605 17.0322 6.93138C18.3659 8.26663 19.0999 10.0414 19.0992 11.9289C19.0976 15.8241 15.9287 18.993 12.0353 18.993ZM15.91 13.7023C15.6976 13.5961 14.6537 13.0823 14.459 13.0114C14.2644 12.9407 14.1227 12.9052 13.9812 13.1178C13.8396 13.3304 13.4326 13.8087 13.3087 13.9505C13.1848 14.0921 13.0609 14.11 12.8486 14.0036C12.6362 13.8973 11.952 13.673 11.1408 12.9495C10.5096 12.3865 10.0834 11.6911 9.95948 11.4784C9.8356 11.2658 9.94623 11.1509 10.0526 11.045C10.1481 10.9498 10.265 10.7969 10.3712 10.6729C10.4774 10.549 10.5127 10.4603 10.5836 10.3186C10.6544 10.1769 10.619 10.0528 10.5658 9.94663C10.5127 9.84024 10.088 8.79495 9.91107 8.36974C9.7387 7.95573 9.56362 8.01175 9.43328 8.0052C9.30956 7.99907 9.16785 7.99768 9.02622 7.99768C8.88467 7.99768 8.65465 8.05083 8.45995 8.26344C8.26533 8.47605 7.71673 8.98982 7.71673 10.0351C7.71673 11.0804 8.47761 12.0901 8.58384 12.2319C8.68997 12.3736 10.0813 14.5186 12.2116 15.4385C12.7181 15.6573 13.1137 15.7879 13.4222 15.8859C13.931 16.0475 14.3939 16.0246 14.7597 15.97C15.1678 15.909 16.0161 15.4562 16.1931 14.9602C16.3701 14.4641 16.3701 14.0389 16.317 13.9503C16.2639 13.8619 16.1224 13.8087 15.91 13.7023Z"
                  className="tw-fill-white"
                />
                <defs>
                  <linearGradient
                    id="paint1_linear_1499_7853"
                    x1="18.2749"
                    y1="-4.38058e-07"
                    x2="6.82169"
                    y2="24"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#61FD7D" />
                    <stop offset="1" stopColor="#27D045" />
                  </linearGradient>
                </defs>
              </svg>
            </a>
          </div>
          <Toast />
        </div>
      </div>
    </div>
  );
};

export default SharingModal;
